import { useCallback, useEffect, useRef, useState } from "react";
import { FloatingBackButton } from "../components/FloatingBackButton";
import { Alert } from "../components/Alert";
import { firebaseAuth } from "../firebase";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { useNavigate } from "../utils";
import { routes } from "../routes";
import { FirebaseError } from "firebase/app";

export const PhoneNumberLogin = () => {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sendEnabled, setSendEnabled] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [helpAlert, setHelpAlert] = useState(false);
  const [badCodeAlert, setBadCodeAlert] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmationResult>();
  const verifier = useRef<RecaptchaVerifier>(undefined);

  const sendCode = useCallback(async () => {
    setSending(true);
    try {
      const rawString = (document.getElementById("phone") as HTMLInputElement)
        .value;
      const result = await signInWithPhoneNumber(
        firebaseAuth,
        `+82 ${rawString.slice(0, 3)}-${rawString.slice(
          3,
          7
        )}-${rawString.slice(7)}`,
        verifier.current
      );
      setConfirm(result);
    } finally {
      setSending(false);
    }
  }, []);

  const signIn = useCallback(async () => {
    try {
      const credential = await confirm?.confirm(
        (document.getElementById("code") as HTMLInputElement).value
      );
      if (credential?.user) {
        navigate(routes.result);
      } else {
        setBadCodeAlert(true);
      }
    } catch (error) {
      if ((error as FirebaseError).code === "auth/invalid-verification-code") {
        setBadCodeAlert(true);
      }
    }
  }, [confirm, navigate]);

  useEffect(() => {
    verifier.current = new RecaptchaVerifier(firebaseAuth, "button-code", {
      size: "invisible",
    });
  }, []);

  return (
    <div className="form">
      <FloatingBackButton />
      <span className="header">휴대폰 번호로 로그인</span>
      <div
        className="input-group"
        style={{ display: "flex", flexDirection: "row" }}
      >
        <input
          id="phone"
          inputMode="tel"
          name="phone"
          style={{ flex: 1 }}
          onChange={(e) => {
            setSendEnabled(e.target.value.length === 11);
          }}
          maxLength={11}
          onSubmit={sendCode}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              sendCode();
            }
          }}
        />
        <button
          className={confirm ? "secondary" : "primary"}
          id="button-code"
          disabled={!sendEnabled || sending}
          style={{ width: 80, marginLeft: 12 }}
          onClick={sendCode}
        >
          {confirm ? "재인증" : "인증"}
        </button>
      </div>
      {confirm && (
        <>
          <input
            onChange={(e) => {
              setSubmitDisabled(e.target.value.length < 6);
            }}
            className="input-group"
            inputMode="numeric"
            name="code"
            id="code"
            placeholder="인증 코드 6자"
            maxLength={6}
            onSubmit={() => {
              signIn();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                signIn();
              }
            }}
          />
          <span
            style={{ alignSelf: "center" }}
            className="input-group"
            onClick={() => {
              setHelpAlert(true);
            }}
          >
            인증 번호를 못 받으셨나요?
          </span>
          <button
            id="submit"
            disabled={submitDisabled}
            className="input-group primary"
            onClick={signIn}
          >
            로그인
          </button>
        </>
      )}
      {helpAlert && (
        <Alert
          title="인증 문자를 못 받으셨나요?"
          desc="국외 발신 문자가 차단되어 있는 경우 인증 문자 수신이 안 될 수도 있습니다. 휴대폰 설정에서 차단을 해제해주세요."
          dismiss={() => setHelpAlert(false)}
        />
      )}
      {badCodeAlert && (
        <Alert
          title="인증 번호를 확인해주세요."
          desc={["인증번호가 일치하지 않습니다.", "다시 입력해주세요."]}
          dismiss={() => setBadCodeAlert(false)}
        />
      )}
    </div>
  );
};
